/*
 * PT-52 - the decision logic behind the two delivery-signal entity cards.
 *
 * Kept as pure functions so the mapping that decides "is this component's main
 * branch green" and "did its quality gate pass" is unit-testable without a
 * browser, a GitLab or a SonarQube. The cards below are presentation only.
 *
 * Both signals are read through the backend proxy (see role 14's
 * app-config.production.yaml), never from the browser directly: the GitLab PAT
 * and the SonarQube token are injected server-side and never reach the client.
 */

import type { Entity } from '@backstage/catalog-model';

/** Annotation carrying the component's full GitLab path, e.g. `internal/fit360-backend`. */
export const GITLAB_SLUG_ANNOTATION = 'gitlab.com/project-slug';

/** Annotation carrying the component's SonarQube projectKey (role 19 uses project_slug). */
export const SONARQUBE_KEY_ANNOTATION = 'sonarqube.org/project-key';

/** Where the catalog read the entity from - the GitLab discovery provider always sets it. */
export const LOCATION_ANNOTATION = 'backstage.io/managed-by-location';

/*
 * PT-54 - both values below are DERIVABLE, so neither card depends on an annotation
 * being present. Role 19 stamps them at onboard time, which left every project
 * onboarded before PT-52 carrying neither and showing no card at all. Backfilling
 * them meant a commit sweep over live team repos (each needing its protected `main`
 * dropped and restored); deriving them is the same information for no repo churn,
 * and it covers every future discovered repo without a stamp.
 *
 * The annotation still WINS when present - a project whose SonarQube key ever
 * diverges from its entity name says so in its catalog-info.yaml and is believed.
 */

/**
 * The component's GitLab path: the annotation, else the repository path inside the
 * location the GitLab discovery provider recorded (it points at the very
 * `catalog-info.yaml` it read, e.g.
 * `url:https://gov-gitlab.example/internal/fit360/-/blob/main/catalog-info.yaml`).
 * `undefined` for anything not discovered from GitLab - a `file:` location, a User,
 * a Group - which is what keeps the cards off those entities.
 */
export function gitlabSlug(entity: Entity): string | undefined {
  const annotated = entity.metadata.annotations?.[GITLAB_SLUG_ANNOTATION];
  if (annotated) return annotated;

  const location = entity.metadata.annotations?.[LOCATION_ANNOTATION] ?? '';
  if (!location.startsWith('url:')) return undefined;
  let path: string;
  try {
    path = new URL(location.slice('url:'.length)).pathname;
  } catch {
    return undefined;
  }
  // `/-/blob/` is GitLab's file-view separator; everything before it is the project.
  // Absent, this is not a GitLab blob URL and we must not guess a slug from it.
  const marker = path.indexOf('/-/blob/');
  if (marker < 0) return undefined;
  return path.slice(0, marker).replace(/^\/+/, '') || undefined;
}

/**
 * The component's SonarQube projectKey: the annotation, else `metadata.name` - role
 * 19 registers the SonarQube project as `project_slug` and names the catalog entity
 * with the same `project_slug`, so for a governed repo they are the same string
 * (`sonar-project.properties` carries that same value as `sonar.projectKey`).
 *
 * Guarded twice, both measured against the live catalog on 2026-08-07:
 *
 *  - on `gitlabSlug`, or the static control-plane Component (a `file:` location, a
 *    GitHub repo, not in this SonarQube) would grow a card claiming a gate it has no
 *    project for;
 *  - on `spec.type === 'service'`, which is what role 19 hardcodes for a governed
 *    project. Without it the guess reaches entities that HAVE a GitLab repo but no
 *    SonarQube project - the `documentation` components role 22/23 bootstrap, and the
 *    `ai-prompt` assets, which are several entities sharing one repo's location. That
 *    was 3 of the 6 components in the catalog, each showing "SonarQube does not know
 *    the project X" - true, useless, and indistinguishable from a real misconfiguration.
 *
 * Neither guard applies to the ANNOTATION: stamp it and any entity of any type is
 * believed, which is the documented way to opt a non-service component in.
 */
export function sonarProjectKey(entity: Entity): string | undefined {
  const annotated = entity.metadata.annotations?.[SONARQUBE_KEY_ANNOTATION];
  if (annotated) return annotated;
  if (entity.spec?.type !== 'service') return undefined;
  return gitlabSlug(entity) ? entity.metadata.name : undefined;
}

/** How a signal renders: which core-components Status* dot, and the words next to it. */
export type Tone = 'ok' | 'error' | 'warning' | 'pending' | 'running' | 'aborted';
export type Signal = { tone: Tone; label: string };

/**
 * GitLab pipeline `status` -> tone. GitLab's vocabulary is closed (see
 * https://docs.gitlab.com/api/pipelines/), but an unknown value must not read as
 * green: anything unrecognised falls through to `warning`, which says "we do not
 * know" rather than "it passed".
 */
const PIPELINE_TONES: Record<string, Signal> = {
  success: { tone: 'ok', label: 'Passed' },
  failed: { tone: 'error', label: 'Failed' },
  running: { tone: 'running', label: 'Running' },
  pending: { tone: 'pending', label: 'Pending' },
  created: { tone: 'pending', label: 'Created' },
  preparing: { tone: 'pending', label: 'Preparing' },
  waiting_for_resource: { tone: 'pending', label: 'Waiting for a runner' },
  scheduled: { tone: 'pending', label: 'Scheduled' },
  manual: { tone: 'warning', label: 'Blocked - waiting on a manual job' },
  canceled: { tone: 'aborted', label: 'Cancelled' },
  canceling: { tone: 'aborted', label: 'Cancelling' },
  skipped: { tone: 'aborted', label: 'Skipped' },
};

export function pipelineSignal(status?: string): Signal {
  const key = (status ?? '').trim().toLowerCase();
  if (!key) return { tone: 'warning', label: 'No status reported' };
  return PIPELINE_TONES[key] ?? { tone: 'warning', label: `Unknown status: ${key}` };
}

/**
 * SonarQube quality-gate `projectStatus.status` -> tone. `NONE` means the project
 * exists but has never been analysed, which is not a pass.
 */
export function qualityGateSignal(status?: string): Signal {
  switch ((status ?? '').trim().toUpperCase()) {
    case 'OK':
      return { tone: 'ok', label: 'Passed' };
    case 'ERROR':
      return { tone: 'error', label: 'Failed' };
    case 'WARN':
      return { tone: 'warning', label: 'Passed with warnings' };
    case 'NONE':
      return { tone: 'warning', label: 'Never analysed' };
    default:
      return { tone: 'warning', label: 'Quality gate status unknown' };
  }
}

/** One condition row of a SonarQube quality gate. */
export type GateCondition = {
  metricKey: string;
  status: string;
  actualValue?: string;
  errorThreshold?: string;
  comparator?: string;
};

const METRIC_LABELS: Record<string, string> = {
  new_coverage: 'Coverage on new code',
  coverage: 'Coverage',
  new_duplicated_lines_density: 'Duplication on new code',
  duplicated_lines_density: 'Duplication',
  new_violations: 'New issues',
  new_blocker_violations: 'New blocker issues',
  new_critical_violations: 'New critical issues',
  new_security_hotspots_reviewed: 'New security hotspots reviewed',
  new_reliability_rating: 'Reliability on new code',
  new_security_rating: 'Security on new code',
  new_maintainability_rating: 'Maintainability on new code',
};

/** `new_coverage` -> `Coverage on new code`; unknown keys degrade to a readable form. */
export function metricLabel(metricKey: string): string {
  const known = METRIC_LABELS[metricKey];
  if (known) return known;
  const words = metricKey.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** `Coverage on new code 71.2 (needs > 80)` - a single readable line per condition. */
export function describeCondition(c: GateCondition): string {
  const label = metricLabel(c.metricKey);
  if (c.actualValue === undefined) return label;
  const threshold =
    c.errorThreshold === undefined
      ? ''
      : ` (needs ${comparatorWord(c.comparator)} ${c.errorThreshold})`;
  return `${label} ${c.actualValue}${threshold}`;
}

/**
 * Sonar's comparator is the direction that makes the gate FAIL (`LT` = fails when
 * less than the threshold), so the requirement shown to a human is its inverse.
 */
function comparatorWord(comparator?: string): string {
  switch ((comparator ?? '').toUpperCase()) {
    case 'LT':
      return '>=';
    case 'GT':
      return '<=';
    case 'EQ':
      return '!=';
    case 'NE':
      return '=';
    default:
      return 'to meet';
  }
}
